import React, { useCallback, useState } from 'react';
import {
  generateUrl,
  getFromAsync,
  removeFromAsync,
  generateLocalizedPath,
} from '../../services/utils';
import dayjs from 'dayjs';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import NewTag from '../buttons/NewTag';
import Images from '../../config/Images';
import IconImage from '../image/IconImage';
import icons from '../../config/assets/icons';
import { ComponentLoading } from '../loading';
import { useTranslation } from 'react-i18next';
import { SOFT_SILVER } from '../../config/color';
import { LOGOUT } from '../../store/login/action';
import { TEXT } from '../../constant/textConstants';
import API_CONSTANT from '../../services/ApiConstant';
import { ROUTE } from '../../constant/routeConstants';
import { AddEditTag, DownloadProcess } from '../modal';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Menu, MenuItem } from '@mui/material';
import TagSummaryModal from '../modal/TagSummaryModal';
import { SESSION_KEY } from '../../constant/appConstant';
import { setSelectedTagData } from '../../store/tags/reducer';
import { encrypt } from '../../services/encryptDecryptService';
import { setSelectedAppData } from '../../store/application/reducer';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

const Sidebar = () => {
  const { lang } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const userData = getFromAsync(SESSION_KEY?.USER_DATA);
  const { app, tag, comman } = useSelector((state) => state);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [isOpen, setIsOpen] = useState(false);
  const [tagData, setTagData] = useState(null);
  const [isTagOpen, setIsTagOpen] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [appDocSummary, setAppDocSummary] = useState([]);
  const [tagDocumentType, setTagDocumentType] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);

  const latestStatusEntry = app?.appDetails?.ApplicationFillingStatusList?.reduce(
    (latest, current) => {
      return new Date(current.AddDateTime) > new Date(latest.AddDateTime) ? current : latest;
    },
  );

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    LOGOUT(dispatch);
    navigate(ROUTE?.LOGIN);
    removeFromAsync(SESSION_KEY.USER_DATA);
  };

  const downloadAndZipMultipleFiles = useCallback(async () => {
    const zip = new JSZip();
    const zipName = `${app?.appDetails?.MarkText || ''}_${app?.appDetails?.ApplicationNumber || ''}_${latestStatusEntry.Status || ''}_${dayjs().format('YYYY_MM_DD_hh:mm')}`;

    try {
      setIsDownload(true);
      const filePromises = app?.appDetails?.ListOfURLs?.map(async (file, index) => {
        const substringText =
          file?.FileURL.substring(file?.FileURL.lastIndexOf('/') + 1) || `file_${index + 1}`;

        // Fetch the file from the URL
        const response = await fetch(API_CONSTANT?.BASE_URL + 'api/' + file?.FileURL);
        if (!response.ok) {
          toast.error(`Failed to fetch ${substringText}: ${response.statusText}`);
        } else {
          const fileData = await response.blob();

          // Detect the file extension from the file URL
          const fileExtension = substringText.split('.').pop() || 'bin';

          // Check if the file name already contains the extension
          const fileNameWithExtension = substringText.includes(`.${fileExtension}`)
            ? substringText
            : `${substringText}.${fileExtension}`;

          zip.file(fileNameWithExtension, fileData);
        }
      });

      await Promise.all(filePromises);

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      saveAs(zipBlob, zipName || 'documents.zip');
      setIsDownload(false);
    } catch (error) {
      setIsDownload(false);
      toast.error(
        error.message || error?.response?.data?.Message || 'Error downloading or zipping files.',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app?.appDetails]);

  return (
    <div className="flex w-[280px] min-w-[280px] max-w-[280px] flex-col border-r-[1px] border-light-grey bg-white">
      <div className="flex items-center gap-2 p-4">
        <IconImage name={Images?.Logo} width={130} height={130} isEye />
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto py-2">
        {!pathname?.includes(ROUTE?.DASHBOARD) &&
        (app?.selectedAppData !== null || tag?.selectedTagData !== null) ? (
          <>
            {comman?.fetchingAppDetails || comman?.fetchingTagDetails ? (
              <div className="flex items-center justify-center py-4">
                <ComponentLoading
                  className="pt-3"
                  loading={comman?.fetchingAppDetails || comman?.fetchingTagDetails}
                />
              </div>
            ) : (
              <>
                <div className="w-full border-b-[1px] text-left">
                  <div className="px-4">
                    {/* App Details */}
                    {app?.appDetails?.ApplicationNumber && (
                      <p className="text-headingMd font-semibold text-charcoal-grey">
                        {app?.appDetails?.ApplicationNumber}
                      </p>
                    )}
                    {app?.appDetails?.AddUser && (
                      <p className="mt-2 text-body1 font-normal text-charcoal-grey">
                        {t(TEXT?.ADDED_BY)} {app?.appDetails?.AddUser}
                      </p>
                    )}

                    {/* Buttons */}
                    <div className="my-3 flex items-center gap-2">
                      {app?.appDetails?.TagId !== 0 ? (
                        <button
                          className="flex w-max items-center gap-2 rounded-3xl bg-lemon-yellow px-5 py-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsOpen(true);
                            setAppDocSummary(app?.appDetails);
                          }}
                        >
                          <icons.tag w={25} h={25} />
                          <p className="text-body1 font-medium">{t(TEXT?.TAG_SUMMARY)}</p>
                        </button>
                      ) : (
                        <NewTag
                          handleClick={() => {
                            setTagDocumentType('AP');
                            setSelectedDocument({ fileName: '' });
                            setTagData({
                              documentId: app?.appDetails?.ApplicationId,
                              tagId: app?.appDetails?.TagId,
                            });
                            setIsTagOpen(true);
                          }}
                          textClassName="text-body1 font-medium"
                          className="flex w-max items-center gap-2 !rounded-full bg-vivid-sky px-5 py-2"
                          iconLeft={<icons.tag w={25} h={25} color="white" />}
                        />
                      )}

                      <button
                        onClick={() => downloadAndZipMultipleFiles()}
                        className="flex w-max items-center gap-2 rounded-3xl bg-bright-green px-3 py-2"
                      >
                        <FileDownloadOutlinedIcon />
                      </button>
                    </div>
                  </div>
                  {/* Contact Details */}
                  <p className="px-4 pb-2 text-body2 font-semibold text-dark-grey-black">
                    {t(TEXT?.CONTACT_DETAILS)}
                  </p>
                </div>
                <div className="mt-2 px-4 text-start">
                  {app?.appDetails?.OwnerName && (
                    <div className="mb-1">
                      <p className="text-body1 font-normal text-charcoal-grey">
                        {t(TEXT?.OWNER_NAME)}
                      </p>
                      <p className="text-body1 font-semibold text-dark-grey-black">
                        {app?.appDetails?.OwnerName}
                      </p>
                    </div>
                  )}
                  {app?.appDetails?.Email && (
                    <div className="mb-1">
                      <p className="text-body1 font-normal text-charcoal-grey">{t(TEXT?.EMAIL)}</p>
                      <p className="text-body1 font-semibold text-dark-grey-black">
                        {app?.appDetails?.Email}
                      </p>
                    </div>
                  )}
                  {app?.appDetails?.City && (
                    <div className="mb-1">
                      <p className="text-body1 font-normal text-charcoal-grey">{t(TEXT?.CITY)}</p>
                      <p className="text-body1 font-semibold text-dark-grey-black">
                        {app?.appDetails?.City}
                      </p>
                    </div>
                  )}
                  {app?.appDetails?.MarkText && (
                    <div className="mb-1">
                      <p className="text-body1 font-normal text-charcoal-grey">
                        {t(TEXT?.MARK_TEXT)}
                      </p>
                      <p className="text-body1 font-semibold text-dark-grey-black">
                        {app?.appDetails?.MarkText}
                      </p>
                    </div>
                  )}
                  {app?.appDetails?.RegularDeadline && (
                    <div className="mb-1">
                      <p className="text-body1 font-normal text-charcoal-grey">
                        {t(TEXT?.DEFAULT_DEADLINE)}
                      </p>
                      <p className="text-body1 font-semibold text-dark-grey-black">
                        {app?.appDetails?.RegularDeadline}
                      </p>
                    </div>
                  )}
                  {app?.appDetails?.ExpressDeadline && (
                    <div className="mb-1">
                      <p className="text-body1 font-normal text-charcoal-grey">
                        {t(TEXT?.RegularDeadline)}
                      </p>
                      <p className="text-body1 font-semibold text-dark-grey-black">
                        <p>{dayjs(app?.appDetails?.ExpressDeadline)?.format('MM/DD/YYYY')}</p>
                      </p>
                    </div>
                  )}
                  {app?.appDetails?.SearchFor && (
                    <div className="mb-1">
                      <p className="text-body1 font-normal text-charcoal-grey">
                        {t(TEXT?.SEARCH_FOR)}
                      </p>

                      <div className="w-full text-left">
                        <div className="space-y-1">
                          {app?.appDetails?.SearchFor?.map((term, index) => (
                            <p
                              className="text-body1 font-semibold text-dark-grey-black"
                              key={index}
                            >
                              {term}
                              {index < app.appDetails.SearchFor.length - 1 && ','}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="px-4 text-left">
            <p className="body1 uppercase text-dark-grey-black">
              {t(TEXT?.NEW)} {t(TEXT?.FORM)}
            </p>
            {/* Form list */}
            {userData?.ApplicationTypeList?.map((list) => (
              <div
                key={list?.ApplicationTypeId}
                className="mt-3 flex w-full cursor-pointer items-center gap-2 rounded-[10px] bg-light-green px-4 py-2.5 hover:bg-light-olive"
                onClick={() => {
                  dispatch(setSelectedAppData(null));
                  dispatch(setSelectedTagData(null));
                  window.open(
                    generateUrl(generateLocalizedPath(lang, ROUTE?.FORM), encrypt({ data: list })),
                    '_blank',
                  );
                }}
              >
                <icons.form w={22} h={22} color="black" />
                <p className="text-subtitle2 font-semibold text-black">
                  {list?.ApplicationTypeName}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Section */}
      {userData && (
        <div className="mt-auto flex items-center gap-3 border-t-[1px] border-light-grey px-4 py-2">
          <div onClick={handleClick} className="flex cursor-pointer items-start gap-3">
            {userData?.WelcomeMessage && (
              <Avatar
                sx={{ width: 45, height: 45, backgroundColor: SOFT_SILVER }}
                alt={userData?.WelcomeMessage?.replace('Hello ', '')?.toUpperCase()}
              />
            )}
            <div className="text-start">
              <p className="text-lg font-semibold capitalize text-black">
                {userData?.WelcomeMessage?.replace('Hello ', '')}
              </p>
              <p className="text-base font-normal text-charcoal-grey">
                {t(TEXT?.LAST_LOGIN_AT)} {userData?.LastLoginAt}
              </p>
            </div>
            <ExpandMoreRoundedIcon />
          </div>

          {open && (
            <Menu
              open={open}
              autoFocus={false}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: -12,
                  ml: -0.3,
                  width: 250,
                  borderRadius: '8px',
                  boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              <MenuItem
                autoFocus={false}
                onClick={() => handleLogout()}
                sx={{
                  '&:hover': {
                    color: 'red',
                    backgroundColor: 'rgba(255, 0, 0, 0.1)',
                  },
                }}
              >
                <p>{t(TEXT?.LOGOUT)}</p>
              </MenuItem>
            </Menu>
          )}
        </div>
      )}

      {isDownload && <DownloadProcess isOpen={isDownload} />}
      {isOpen && <TagSummaryModal data={appDocSummary} isOpen={isOpen} setIsOpen={setIsOpen} />}

      {isTagOpen && (
        <AddEditTag
          data={tagData}
          isOpen={isTagOpen}
          tagFor={tagDocumentType}
          setIsOpen={setIsTagOpen}
          setSelectedDocument={setSelectedDocument}
          title={{ selectedDocument, marktext: app?.appDetails?.MarkText }}
        />
      )}
    </div>
  );
};

export default Sidebar;
